import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/logo.png";

const FadeIn = ({ children, delay = 0, className = "" }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
};

const SocialLink = ({ href, label, icon }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        className="group relative w-11 h-11 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 overflow-hidden transition-all duration-300 hover:border-amber-500/50 hover:text-amber-400 hover:-translate-y-1 hover:shadow-lg hover:shadow-amber-500/20"
    >
        <span className="absolute inset-0 bg-amber-500/10 scale-0 group-hover:scale-100 transition-transform duration-300 rounded-full" />
        <i className={`bi ${icon} relative z-10 text-sm transition-transform duration-300 group-hover:scale-110`} aria-hidden="true" />
    </a>
);

const FooterLink = ({ to, children }) => (
    <Link
        to={to}
        className="group flex items-center gap-3 text-sm text-slate-400 hover:text-white transition-colors duration-200 py-1"
    >
        <span className="w-0 h-[2px] bg-amber-500 rounded-full group-hover:w-4 transition-all duration-300" />
        <span className="group-hover:translate-x-1 transition-transform duration-300">{children}</span>
    </Link>
);

const Newsletter = () => {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle"); // idle | sending | success

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email) return;
        setStatus("sending");
        setTimeout(() => {
            setStatus("success");
            setEmail("");
            setTimeout(() => setStatus("idle"), 3000);
        }, 1200);
    };

    return (
        <FadeIn delay={150}>
            <div className="relative p-6 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700/50 overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 blur-3xl rounded-full pointer-events-none" />
                <h4 className="text-white font-bold mb-2 relative z-10">Stay Updated</h4>
                <p className="text-xs text-slate-400 mb-4 relative z-10">
                    Get insights on EFRIS, SAP, and digital transformation in East Africa.
                </p>
                <form onSubmit={handleSubmit} className="relative z-10 flex gap-2">
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        className="flex-1 min-w-0 bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-600 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/30 transition-all"
                    />
                    <button
                        type="submit"
                        disabled={status !== "idle"}
                        className="px-4 py-2.5 rounded-xl bg-amber-500 text-slate-950 font-bold text-sm hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                    >
                        {status === "sending" ? (
                            <i className="bi bi-arrow-repeat animate-spin" aria-hidden="true" />
                        ) : status === "success" ? (
                            <i className="bi bi-check-lg" aria-hidden="true" />
                        ) : (
                            <i className="bi bi-send-fill" aria-hidden="true" />
                        )}
                    </button>
                </form>
            </div>
        </FadeIn>
    );
};

export default function Footer() {
    const [showTop, setShowTop] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);

    useEffect(() => {
        const onScroll = () => {
            setShowTop(window.scrollY > 600);
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            setScrollProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const services = [
        "Custom Software Development",
        "Mobile & Web Applications",
        "SAP Business One Solutions",
        "EFRIS Integration",
        "Cloud & IT Infrastructure",
        "Cybersecurity Solutions",
    ];

    const company = [
        { name: "About Us", link: "/about" },
        { name: "Services", link: "/services" },
        { name: "Solutions", link: "/industries" },
        { name: "Contact", link: "/contact" },
    ];

    const legal = [
        { name: "Privacy Policy", link: "/privacy" },
        { name: "Data Deletion Policy", link: "/delete-policy" },
        { name: "Terms of Service", link: "/terms" },
    ];

    return (
        <footer className="relative bg-slate-950 text-slate-400 border-t border-slate-800 overflow-hidden">
            {/* Ambient background effects */}
            <div className="absolute top-0 left-1/4 w-[500px] h-[400px] bg-amber-500/5 blur-[120px] rounded-full pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-[400px] h-[300px] bg-blue-600/5 blur-[100px] rounded-full pointer-events-none" />

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-6 pt-20 pb-8 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">

                    {/* Brand Column */}
                    <div className="lg:col-span-4 space-y-6">
                        <FadeIn>
                            <Link to="/" className="flex items-center gap-3 group">
                                <div className="relative">
                                    <img
                                        src={logo}
                                        alt="Delight Galaxy"
                                        className="w-12 h-12 object-contain drop-shadow-lg group-hover:scale-105 transition-transform duration-300"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                    <div className="hidden w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 items-center justify-center text-white font-black text-lg shadow-lg">
                                        D
                                    </div>
                                </div>
                                <div>
                                    <h2 className="text-xl font-black text-white leading-none tracking-tight">Delight Galaxy</h2>
                                    <p className="text-[10px] tracking-[0.25em] uppercase text-amber-500 font-bold mt-1">Software Solutions</p>
                                </div>
                            </Link>
                        </FadeIn>

                        <FadeIn delay={100}>
                            <p className="text-sm leading-relaxed text-slate-500 max-w-sm">
                                Building secure digital solutions through custom software, enterprise systems,
                                integrations, and modern IT infrastructure for growing organizations across East Africa.
                            </p>
                        </FadeIn>

                        <FadeIn delay={150}>
                            <div className="flex gap-3">
                                <SocialLink href="https://linkedin.com" label="LinkedIn" icon="bi-linkedin" />
                                <SocialLink href="https://github.com" label="GitHub" icon="bi-github" />
                                <SocialLink href="https://x.com" label="X (Twitter)" icon="bi-twitter-x" />
                                <SocialLink href="https://facebook.com" label="Facebook" icon="bi-facebook" />
                            </div>
                        </FadeIn>

                        <FadeIn delay={200}>
                            <Newsletter />
                        </FadeIn>
                    </div>

                    {/* Solutions */}
                    <div className="lg:col-span-2 lg:col-start-6">
                        <FadeIn delay={100}>
                            <h3 className="text-white font-bold uppercase tracking-widest text-xs mb-6 flex items-center gap-2">
                                <span className="w-4 h-[2px] bg-amber-500 rounded-full" />
                                Solutions
                            </h3>
                            <ul className="space-y-3">
                                {services.map((item) => (
                                    <li key={item}>
                                        <FooterLink to="/services">{item}</FooterLink>
                                    </li>
                                ))}
                            </ul>
                        </FadeIn>
                    </div>

                    {/* Company & Legal */}
                    <div className="lg:col-span-2">
                        <FadeIn delay={200}>
                            <h3 className="text-white font-bold uppercase tracking-widest text-xs mb-6 flex items-center gap-2">
                                <span className="w-4 h-[2px] bg-amber-500 rounded-full" />
                                Company
                            </h3>
                            <ul className="space-y-3 mb-10">
                                {company.map((item) => (
                                    <li key={item.name}>
                                        <FooterLink to={item.link}>{item.name}</FooterLink>
                                    </li>
                                ))}
                            </ul>
                        </FadeIn>

                        <FadeIn delay={300}>
                            <h3 className="text-white font-bold uppercase tracking-widest text-xs mb-6 flex items-center gap-2">
                                <span className="w-4 h-[2px] bg-amber-500 rounded-full" />
                                Legal
                            </h3>
                            <ul className="space-y-3">
                                {legal.map((item) => (
                                    <li key={item.name}>
                                        <FooterLink to={item.link}>{item.name}</FooterLink>
                                    </li>
                                ))}
                            </ul>
                        </FadeIn>
                    </div>

                    {/* Contact */}
                    <div className="lg:col-span-3">
                        <FadeIn delay={400}>
                            <h3 className="text-white font-bold uppercase tracking-widest text-xs mb-6 flex items-center gap-2">
                                <span className="w-4 h-[2px] bg-amber-500 rounded-full" />
                                Contact
                            </h3>
                            <address className="space-y-4 text-sm not-italic mb-8">
                                <div className="flex items-start gap-3 group">
                                    <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-amber-500 shrink-0 group-hover:border-amber-500/30 transition-colors">
                                        <i className="bi bi-geo-alt" aria-hidden="true" />
                                    </div>
                                    <div>
                                        <p className="text-white font-medium text-xs uppercase tracking-wider mb-0.5">Headquarters</p>
                                        <p className="text-slate-400">Kampala, Uganda</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3 group">
                                    <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-amber-500 shrink-0 group-hover:border-amber-500/30 transition-colors">
                                        <i className="bi bi-envelope" aria-hidden="true" />
                                    </div>
                                    <div>
                                        <p className="text-white font-medium text-xs uppercase tracking-wider mb-0.5">Email</p>
                                        <a href="mailto:smuwanguzi2@gmail.com" className="text-slate-400 hover:text-amber-400 transition-colors">
                                            smuwanguzi2@gmail.com
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3 group">
                                    <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-amber-500 shrink-0 group-hover:border-amber-500/30 transition-colors">
                                        <i className="bi bi-telephone" aria-hidden="true" />
                                    </div>
                                    <div>
                                        <p className="text-white font-medium text-xs uppercase tracking-wider mb-0.5">Phone</p>
                                        <a href="tel:+256704698298" className="text-slate-400 hover:text-amber-400 transition-colors">
                                            +256 704 698298
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3 group">
                                    <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-amber-500 shrink-0 group-hover:border-amber-500/30 transition-colors">
                                        <i className="bi bi-clock" aria-hidden="true" />
                                    </div>
                                    <div>
                                        <p className="text-white font-medium text-xs uppercase tracking-wider mb-0.5">Working Hours</p>
                                        <p className="text-slate-400">Mon - Fri: 8:00 - 18:00<br />Sat: 9:00 - 14:00</p>
                                    </div>
                                </div>
                            </address>

                            <a
                                href="#/contact"
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-500 text-slate-950 font-bold text-sm hover:bg-amber-400 hover:shadow-lg hover:shadow-amber-500/20 hover:-translate-y-0.5 transition-all duration-300"
                            >
                                Talk To An Expert
                                <i className="bi bi-arrow-right" aria-hidden="true" />
                            </a>
                        </FadeIn>
                    </div>
                </div>

                {/* Trust Badges */}
                <FadeIn delay={500}>
                    <div className="mt-16 mb-8 flex flex-wrap items-center justify-center gap-6 md:gap-10">
                        {[
                            { icon: "bi-shield-check", label: "ISO 27001 Ready" },
                            { icon: "bi-receipt", label: "URA Certified Partner" },
                            { icon: "bi-globe-africa", label: "East Africa Coverage" },
                            { icon: "bi-headset", label: "24/7 Support" },
                        ].map((badge) => (
                            <div key={badge.label} className="flex items-center gap-2 text-xs text-slate-500">
                                <i className={`bi ${badge.icon} text-amber-500/80`} aria-hidden="true" />
                                <span className="uppercase tracking-wider font-medium">{badge.label}</span>
                            </div>
                        ))}
                    </div>
                </FadeIn>

                {/* Bottom Bar */}
                <div className="pt-8 border-t border-slate-900 flex flex-col md:flex-row justify-between items-center gap-4 text-xs">
                    <p className="text-slate-600">
                        © {new Date().getFullYear()} Delight Galaxy Limited. All rights reserved.
                    </p>
                    <p className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600 font-semibold tracking-wide">
                        Engineering Digital Solutions For Tomorrow.
                    </p>
                </div>
            </div>

            {/* Back to Top */}
            <button
                onClick={scrollToTop}
                aria-label="Back to top"
                className={`fixed bottom-8 right-8 w-12 h-12 rounded-full bg-slate-900 border border-slate-700 text-amber-500 flex items-center justify-center shadow-xl hover:bg-amber-500 hover:text-slate-950 hover:border-amber-500 hover:-translate-y-1 transition-all duration-300 z-40 group ${showTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
            >
                {/* Progress ring */}
                <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 36 36">
                    <path
                        className="text-slate-800"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    />
                    <path
                        className="text-amber-500 transition-all duration-100"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeDasharray={`${scrollProgress}, 100`}
                    />
                </svg>
                <i className="bi bi-arrow-up relative z-10 text-sm font-bold group-hover:animate-bounce" aria-hidden="true" />
            </button>
        </footer>
    );
}